import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo, useState } from 'react'
import {
	useFieldArray,
	useForm,
	useWatch,
	type FieldErrors,
} from 'react-hook-form'
import { treatmentSchema } from '../components/validation/TreatmentValidation'
import { TreatmentFormFields } from '../types/types'

type TreatmentFormValues = {
	treatments: TreatmentFormFields[]
}

export const useTreatmentForm = (
	initialState: TreatmentFormFields[],
	isEdit: boolean,
) => {
	const [submittedOnce, setSubmittedOnce] = useState(false)
	const [validatedCount, setValidatedCount] = useState(0)

	const {
		handleSubmit,
		control,
		setValue,
		formState: { errors },
		reset,
	} = useForm<TreatmentFormValues>({
		mode: 'onSubmit',
		defaultValues: { treatments: initialState },
		resolver: yupResolver(treatmentSchema) as any,
	})

	const { append, remove } = useFieldArray({ control, name: 'treatments' })
	const currentFields = useWatch({ control, name: 'treatments' })

	useEffect(() => {
		reset({ treatments: initialState })
		setSubmittedOnce(false)
		setValidatedCount(0)
	}, [initialState, reset])

	const shouldShowError = (
		field: keyof TreatmentFormFields,
		index: number,
		errors: FieldErrors<TreatmentFormValues>,
	) => {
		return (
			submittedOnce &&
			index < validatedCount &&
			!!errors.treatments?.[index]?.[field]
		)
	}

	const fields = useMemo(() => currentFields ?? [], [currentFields])

	const isPositiveInt = (value: unknown) => {
		const text = String(value ?? '').trim()
		return /^\d+$/.test(text) && Number(text) >= 1
	}

	const isFieldComplete = (field: TreatmentFormFields) =>
		String(field.title || '').trim() !== '' &&
		String(field.description || '').trim() !== '' &&
		isPositiveInt(field.duration) &&
		isPositiveInt(field.examinationInterval) &&
		(field.selectedDiagnose || []).length > 0 &&
		(!field.rejectMilk || isPositiveInt(field.milkRejectDays))

	const canAdd = useMemo(() => fields.every(isFieldComplete), [fields])

	const canSubmit = useMemo(() => {
		if (!isEdit) return canAdd

		const isChanged = JSON.stringify(fields) !== JSON.stringify(initialState)
		return canAdd && isChanged
	}, [canAdd, fields, initialState, isEdit])

	const handleFormSubmit = (
		e: React.FormEvent<HTMLFormElement>,
		onSubmit: (data: TreatmentFormValues) => void,
	) => {
		e.preventDefault()
		setSubmittedOnce(true)
		setValidatedCount(fields.length)
		handleSubmit(onSubmit)()
	}

	return {
		control,
		setValue,
		errors,
		append,
		remove,
		reset,
		currentFields: fields,
		canAdd,
		canSubmit,
		handleFormSubmit,
		shouldShowError: (field: keyof TreatmentFormFields, index: number) =>
			shouldShowError(field, index, errors),
	}
}
